import React, {useMemo} from "react";
import {
    AlertCircle,
    CheckCircle,
    Clock,
    DollarSign,
    TrendingDown,
    TrendingUp
} from "lucide-react";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Typography
} from "@mui/material";
import {formatMoney, getDiasRestantes} from "./licencasUtils";
import {useLicencas} from "./useLicencas";

export default function LicencasDashboardPage() {
    const {rows, loading, feedback} = useLicencas();

    const metrics = useMemo(() => {
        const ativas = rows.filter((item) => item.status === 'ATIVA').length;
        const atraso = rows.filter((item) => item.status === 'EM_ATRASO').length;
        const expirando = rows.filter((item) => {
            const dias = getDiasRestantes(item.validade_fim);
            return item.status === 'ATIVA' && dias != null && dias > 0 && dias <= 30;
        }).length;
        const total = rows.length;
        const receitaMensal = rows
            .filter((item) => ['ATIVA', 'EM_ATRASO'].includes(item.status))
            .reduce((acc, item) => acc + (Number(item.plano?.preco_base_mensal) || 0), 0);
        const receitaEmRisco = rows
            .filter((item) => ['EM_ATRASO', 'SUSPENSA'].includes(item.status))
            .reduce((acc, item) => acc + (Number(item.plano?.preco_base_mensal) || 0), 0);
        const renovacao = total ? Math.round((ativas / total) * 100) : 0;

        return {ativas, atraso, expirando, receitaMensal, receitaEmRisco, renovacao};
    }, [rows]);

    const proximas = useMemo(() => rows
        .filter((item) => {
            const dias = getDiasRestantes(item.validade_fim);
            return item.status === 'ATIVA' && dias != null && dias > 0 && dias <= 15;
        })
        .sort((a, b) => new Date(a.validade_fim).getTime() - new Date(b.validade_fim).getTime())
        .slice(0, 6), [rows]);

    const atrasadas = useMemo(() => rows.filter((item) => item.status === 'EM_ATRASO').slice(0, 6), [rows]);

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h4" fontWeight={700}>Visão executiva de licenças</Typography>
                <Typography variant="body1" color="text.secondary">
                    Acompanhe saúde da base, risco de receita e renovações.
                </Typography>
            </Box>

            {feedback.error ? <Alert severity="error">{feedback.error}</Alert> : null}

            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)'}, gap: 2}}>
                {[
                    {
                        label: 'Licenças Ativas',
                        value: metrics.ativas,
                        icon: CheckCircle,
                        trend: '+12%',
                        trendUp: true,
                        iconBg: '#dcfce7',
                        iconColor: '#15803d',
                    },
                    {
                        label: 'Receita Recorrente Mensal',
                        value: formatMoney(metrics.receitaMensal),
                        icon: DollarSign,
                        trend: '+8%',
                        trendUp: true,
                        iconBg: '#ccfbf1',
                        iconColor: '#0f766e',
                    },
                    {
                        label: 'Em Atraso',
                        value: metrics.atraso,
                        icon: AlertCircle,
                        trend: '-3%',
                        trendUp: false,
                        iconBg: '#fee2e2',
                        iconColor: '#b91c1c',
                    },
                    {
                        label: 'Expirando (30 dias)',
                        value: metrics.expirando,
                        icon: Clock,
                        trend: '+5%',
                        trendUp: true,
                        iconBg: '#fef3c7',
                        iconColor: '#b45309',
                    },
                ].map((item) => {
                    const Icon = item.icon;
                    return (
                        <Paper key={item.label} variant="outlined" sx={{p: 2.5, borderRadius: 3}}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{mb: 1.5}}>
                                <Box sx={{width: 44, height: 44, borderRadius: 2, bgcolor: item.iconBg, color: item.iconColor, display: 'grid', placeItems: 'center'}}>
                                    <Icon size={22} />
                                </Box>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: item.trendUp ? '#16a34a' : '#dc2626'}}>
                                    {item.trendUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                    <Typography variant="caption" fontWeight={600}>{item.trend}</Typography>
                                </Stack>
                            </Stack>
                            <Typography variant="h5" fontWeight={700}>{item.value}</Typography>
                            <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                        </Paper>
                    );
                })}
            </Box>

            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2}}>
                <Paper variant="outlined" sx={{borderRadius: 3, overflow: 'hidden'}}>
                    <Box sx={{px: 2.5, py: 1.5, bgcolor: '#fef9c3', borderBottom: (t) => `1px solid ${t.palette.divider}`}}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Clock size={18} color="#a16207" />
                            <Typography variant="subtitle1" fontWeight={700}>Próximas do Vencimento</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{color: '#a16207'}}>
                            Licenças que expiram nos próximos 15 dias
                        </Typography>
                    </Box>
                    <Box sx={{p: 2.5}}>
                        <Stack spacing={1.5}>
                            {loading ? (
                                <Box sx={{display: 'grid', placeItems: 'center', py: 2}}>
                                    <CircularProgress size={20} />
                                </Box>
                            ) : null}
                            {!proximas.length && !loading ? (
                                <Typography color="text.secondary">Nenhuma licença crítica.</Typography>
                            ) : null}
                            {proximas.map((item) => {
                                const dias = getDiasRestantes(item.validade_fim);
                                return (
                                    <Box key={item.id} sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                        <Box>
                                            <Typography fontWeight={600}>{item.empresa?.razao_social || item.empresa?.nome || '-'}</Typography>
                                            <Typography variant="body2" color="text.secondary">{item.plano?.nome_plano || '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Vence em {item.validade_fim_fmt}
                                            </Typography>
                                        </Box>
                                        <Chip size="small" color={dias <= 7 ? 'error' : 'warning'} label={`${dias} dia(s)`} />
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                </Paper>

                <Paper variant="outlined" sx={{borderRadius: 3, overflow: 'hidden'}}>
                    <Box sx={{px: 2.5, py: 1.5, bgcolor: '#fee2e2', borderBottom: (t) => `1px solid ${t.palette.divider}`}}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <AlertCircle size={18} color="#b91c1c" />
                            <Typography variant="subtitle1" fontWeight={700}>Licenças em Atraso</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{color: '#b91c1c'}}>
                            Foco para recuperação de receita
                        </Typography>
                    </Box>
                    <Box sx={{p: 2.5}}>
                        <Stack spacing={1.5}>
                            {loading ? (
                                <Box sx={{display: 'grid', placeItems: 'center', py: 2}}>
                                    <CircularProgress size={20} />
                                </Box>
                            ) : null}
                            {!atrasadas.length && !loading ? (
                                <Typography color="text.secondary">Nenhuma licença em atraso.</Typography>
                            ) : null}
                            {atrasadas.map((item) => (
                                <Box key={item.id} sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                    <Box>
                                        <Typography fontWeight={600}>{item.empresa?.razao_social || item.empresa?.nome || '-'}</Typography>
                                        <Typography variant="body2" color="text.secondary">Cobrança: {item.data_proxima_cobranca_fmt}</Typography>
                                    </Box>
                                    <Chip icon={<AlertCircle size={14} />} size="small" color="error" label={item.status} />
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Paper>
            </Box>

            <Paper variant="outlined" sx={{p: 2.5, borderRadius: 3}}>
                <Typography variant="h6" fontWeight={700}>Resumo Financeiro</Typography>
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'}, gap: 2, mt: 2}}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Receita Confirmada (MRR)</Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: '#15803d'}}>{formatMoney(metrics.receitaMensal)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Receita em Risco</Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: '#dc2626'}}>{formatMoney(metrics.receitaEmRisco)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Taxa de Renovação</Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: '#0f766e'}}>{metrics.renovacao}%</Typography>
                    </Box>
                </Box>
            </Paper>
        </Stack>
    );
}
